import React, {useContext, useEffect, useState} from 'react';
import {TableColumn, TableSettings} from "../../../tableSettings";
import AbstractListView, {
  DataFetchResult,ExportRowRenderer,ListCustomFilterRenderer,ListOrderBy,ListRowRenderer
} from "../../common/AbstractListView";
import constants from '../../../constants';
import {vesselInfoList,} from '../tableSettings';
import {Search} from "../../../filtering";
import {
  DateRenderer,DateTimeRenderer,NullableCheckboxIconRenderer,
  TextAlignLeftRenderer,TextRenderer
} from '../../../uiControls/SmartTableRenderers';
import JobListViewContext, { RightClickContext } from '../../common/JobListViewContext';
import useFunctionKeys from '../../../hooks/useFunctionKeys';
import PrimaryButton from '../../../ui/PrimaryButton';
import {JobNames} from '../../../framework.core/JobNames';
import {OiVesselListItem} from '../../../fms.data/models/OiVesselListItem';
import * as XCol from '../../../framework.core/XExportColumn';
import IfmsModal from '../../../ui/IfmsModal';
import IfmsDate from '../../../ui/Date';
import SecondaryButton from '../../../ui/SecondaryButton';
import CheckBoxUncontrolled from '../../../ui/CheckBoxUncontrolled';
import LeftRightSplit from '../../../ui/LeftRightSplit';
import Label from '../../../ui/Label';
import ListViewShortcutMenu from '../../common/ListViewShortcutMenu';
import useReportViewer from '../../../hooks/useReportViewer';
import { VesselAcctArgs } from '../../../fms.report/args';
import { DocumentType } from '../../../framework.core/DocumentType';
import { ActionButtonItem, borderColors, splitMenuItemsIntoSections } from '../../../ui/ActionButton';
import ToolboxMenuItem from '../../../uiControls/ToolboxMenuItem';
import Menu, { IfmsMenuItem } from '../../../ui/menu/Menu';
import ApplicationContext from '../../../ApplicationContext';
import useGlobalOkDialog from '../../../hooks/useGlobalOkDialog';

interface ChangeEtaModel {
  id: number;
  eta: string;
  isReminder: boolean;
};

function RightClickMenu({dismiss}:{dismiss: VoidFunction}) {
  const jobListContext = useContext(JobListViewContext);
  // const selectedElement = jobListContext.selectedElement as OceanQuoteListItem;
  // const appContext = useContext(ApplicationContext);
  // const [showGlobalOkDialog] = useGlobalOkDialog();

  const items = [
    { label: 'Search', shortcut: 'F5', separator: true, newCol: false },
  ];

  const menuItems: ActionButtonItem[] = items.map((i, index) =>({
    label: <ToolboxMenuItem width="190px" label={i.label} shortcut={i.shortcut} />,
    separator: i.separator,
    selected: false,
    newCol: i.newCol,
    index,
  }));

  function onMenuItemSelected(item:IfmsMenuItem) {
    switch (item.index) {
      case 0:
        jobListContext.triggerTableSearch('F5');
        break;
      case 1:
        jobListContext.reloadData();
        break;
      default:
        break;
    }
    dismiss();
  }

  return (
    <Menu
      variant="plain"
      specialMenu='rightClickContext'
      items={splitMenuItemsIntoSections(menuItems)}
      mode="vertical"
      onMenuItemSelected={onMenuItemSelected}
      subMenuStyle={{
        minWidth: 150,
        whiteSpace: 'nowrap',
      }}
      menuStyle={(i: number) => ({
        maxHeight: '300px',
        overflowY: 'hidden',
        padding: 0,
        borderTop: `3px solid ${borderColors[i % borderColors.length]}`,
        paddingTop: 10,
      })}
      dismiss={dismiss}
    />
  );
}

function useSelectedVi() {
  const jobListContext = useContext(JobListViewContext);
  const selectedElement = jobListContext.selectedElement as OiVesselListItem|null;

  return { selectedElement };
}

function SelectedViEta({
  changeEtaModel,
  onChange
}: {
  changeEtaModel: ChangeEtaModel,
  onChange: (x: Partial<ChangeEtaModel>) => void,
}) {
  useEffect(() => {
    const focusElement = document.getElementById(`${changeEtaModel.id}-eta`) as HTMLInputElement;

    if (focusElement) {
      setTimeout(() => {
        focusElement.focus();
        focusElement.select();
      }, 100);
    }
  },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  return <IfmsDate name='vesselInfo-detail-eta' dateId={`${changeEtaModel.id}-eta`} value={changeEtaModel.eta} onChange={(date) => onChange({eta: date??''})} />;
}

function ViListCommands() {
  const { selectedElement } = useSelectedVi();
  const [showEtaEdit, setShowEtaEdit] = useState(false);
  const appContext = useContext(ApplicationContext);
  const jobListContext = useContext(JobListViewContext);
  const [showGlobalOkDialog] = useGlobalOkDialog();
  const {viewReport} = useReportViewer();
  const [changeEtaModel, setChangeEtaModel] = useState<ChangeEtaModel>({id: 0, eta: '', isReminder: false});

  function onChange(change: Partial<ChangeEtaModel>) {
    setChangeEtaModel(prevState => ({...prevState, ...change}));
  }

  useEffect(() => {
    setChangeEtaModel({id: selectedElement?.id??0, eta: selectedElement?.eta??'', isReminder: selectedElement?.isReminder??false})
  }, [selectedElement])
  
  async function print() {
    if (!selectedElement) return;
    
    const title = "Vessel Account Report";
    const args = new VesselAcctArgs({
      formCaption: title,
      subject: title,
      docType: DocumentType.VesselAcct,
      vesselName: selectedElement.vessel,
      voyage: selectedElement.voyage
    });
    viewReport(args, "", true);
  }

  async function changeETA() {
    if (!selectedElement) return;
    
    const apiResult = await appContext.api.setOIetaByVessel(changeEtaModel.eta, selectedElement.vessel, selectedElement.voyage, 
      selectedElement.discharge, selectedElement.eta, changeEtaModel.isReminder);
    if(!apiResult || !apiResult.success) {
      await showGlobalOkDialog(<p>Failed !</p>);
      return
    } 

    await showGlobalOkDialog(<p>Successful !</p>);
    setShowEtaEdit(false);
    jobListContext.reloadData();
  }

  return (
    <div className={!appContext.useDarkMode ? "ifms-common-vertical-separator" : "ifms-common-vertical-separator-darkmode"}>
      <div className="ifms-common-no-vertical-separator">
        <PrimaryButton
          size="regular"
          disabled={!selectedElement}
          onClick={() => setShowEtaEdit(true)}
          name='vesselInfo-list-etaChange-button'
        >
            ETA Change
        </PrimaryButton>&nbsp;
        <PrimaryButton name='vesselInfo-list-billingStatus-button' size="regular" onClick={print}>Billing Status</PrimaryButton>
      </div>
      <div >
        {(showEtaEdit && selectedElement) &&
          <IfmsModal isBlueHeader onCancel={() => setShowEtaEdit(false)} draggable isOpen={showEtaEdit} title="ETA Change" width="180px">
            <SelectedViEta changeEtaModel={changeEtaModel} onChange={onChange}/>
            <LeftRightSplit
              leftSize="25px"
              rightSize="calc(100% - 25px)"
              left={<CheckBoxUncontrolled initialValue={changeEtaModel.isReminder} onChange={value => { onChange({ isReminder: value }) }} />}
              right={<Label label="Send Notice to OP" />}
            />
            <div style={{
              display: 'flex',
              justifyContent: 'space-evenly',
              alignItems: 'center',
              marginTop: '20px',
            }}>
              <SecondaryButton
                size="regular"
                name='vesselInfo-detail-cancel'
                onClick={() => {
                  setShowEtaEdit(false);
                }}>
                Cancel
              </SecondaryButton>
              <SecondaryButton
                size="regular"
                name='vesselInfo-detail-ok'
                onClick={() => changeETA()}>
                Ok
              </SecondaryButton>
            </div>
          </IfmsModal>
        }
      </div>
    </div>
  );
}

function ViListToolbar() {
  return (
    <>
      <ListViewShortcutMenu />
    </>
  );
}

function formatData(data:Array<OiVesselListItem>) {
  return data;
}

function ViListViewExtras() {
  useFunctionKeys({onFunction: onFunctionKey});

  const jobListContext = useContext(JobListViewContext);

  const [unprocessedFkey, setUnprocessedFkey] = useState<string|null>(null);

  useEffect(() => {
      if (!unprocessedFkey) {
        return;
      }

      doProcessFkey();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [unprocessedFkey]
  );

  function doProcessFkey() {
    if (unprocessedFkey === "F11") {
      jobListContext.reloadData();
    }

    setUnprocessedFkey(null);
  }

  function onFunctionKey(fkey:string) {
    if (['F11'].indexOf(fkey) >= 0) {
      setUnprocessedFkey(fkey);
      return true;
    }

    return false;
  }

  return (
    <></>
  );
}

/**
 * Concrete implementation for Vessel Status List screen
 *
 * @class VesselInfoListView
 * @extends {AbstractListView}
 */
 class VesselInfoListView extends AbstractListView {

  async fetchAndFormatData(api: any, search: Search): Promise<DataFetchResult> {
    const apiResult = await api.getListItems(JobNames.OIVessel, 'OiVesselListItem',
    OiVesselListItem.calculateSearchParameters(search, OiVesselListItem.MapSearchFields),
      1,
      search.pageSize);

    if (!apiResult || !apiResult.entities || apiResult.entities.length <= 0) {
      return {data: [], totalCount: 0};
    }

    return { data: formatData(apiResult.entities), totalCount: apiResult.totalCount};
  }

  getCustomFilterRenderers(): ListCustomFilterRenderer[] {
    return [
    ];
  }

  getInfoAreaEntityId() {
    return constants.infoAreaEntity.oceanImportMbl;
  }

  getRowRenderers(): ListRowRenderer[] {
    return OiVesselListItem.getExportCollection().map(x => {
      const colRenderer:ListRowRenderer = { accessor: x.dataIndex, Cell: TextAlignLeftRenderer };
      if(x.align === XCol.XAlignType.Center) colRenderer.Cell = TextRenderer;
      if(x.dataIndex === "status") {
        colRenderer.Cell = () => <span></span>
        colRenderer.Header = () => <span></span>;
      }
      else if(x.type === XCol.XCellType.Bool || x.type === XCol.XCellType.StrBool) colRenderer.Cell = NullableCheckboxIconRenderer;
      else if(x.type === XCol.XCellType.Date) colRenderer.Cell = DateRenderer;
      else if(x.type === XCol.XCellType.DateTime) colRenderer.Cell = DateTimeRenderer;
      return colRenderer;
    });
  }
  
  getExportRenderers(): ExportRowRenderer[] {
    const colRenderer = [];
    colRenderer.push({accessor: 'status', changedValue: (value: any)=> { return value['status'] === 'PC' ? 'Period Close' : ''}});
    return colRenderer;
  }

  getDefaultOrderBy(): ListOrderBy {
    return {
      orderBy: 'id',
      orderByAscending: false,
    };
  }

  getTableSettings(): TableSettings {
    const tsettings = vesselInfoList;
    const cols = OiVesselListItem.getExportCollection();
    tsettings.columns = cols.map<TableColumn>(x => {
      return { ...x, accessor: x.dataIndex, Header: x.text, filterType: undefined, filterTypeV2: x.type};
    });
    
    return tsettings;
  }

  renderCommands() {
    return (
      <ViListCommands />
    );
  }

  renderBeforeConfigureButton() {
    return (
      <ViListToolbar />
    );
  }


  renderExtras() {
    return <ViListViewExtras />
  }

  getViewUrlFor() {
    return '';
  }

  getListViewTitle() {
    return 'Vessel Info List';
  };

  hasMemo(): boolean {
    return false;
  }
  
  hasRightClickContext(): boolean {
    return true;
  }

  renderRightClickContext(context: RightClickContext, dismiss: VoidFunction): React.ReactNode {
    return <RightClickMenu dismiss={dismiss} />;
  }
};

export default VesselInfoListView;